// This file simulates a backend API for pricing data
// In a real application, this would be fetched from a server

// Define pricing tiers based on zip code regions
export interface ZipCodeRegion {
  name: string;
  zipCodes: string[];
  basePriceMultiplier: number;
}

export const zipCodeRegions: ZipCodeRegion[] = [
  {
    name: 'Urban Core',
    zipCodes: ['10001', '10002', '10003', '10004', '10005', '10006', '10007', '10008', '10009', '10010'],
    basePriceMultiplier: 1.2
  },
  {
    name: 'Urban Residential',
    zipCodes: ['10011', '10012', '10013', '10014', '10015', '10016', '10017', '10018', '10019', '10020'],
    basePriceMultiplier: 1.1
  },
  {
    name: 'Suburban',
    zipCodes: ['10021', '10022', '10023', '10024', '10025', '10026', '10027', '10028', '10029', '10030'],
    basePriceMultiplier: 1.0
  },
  {
    name: 'Rural',
    zipCodes: ['10031', '10032', '10033', '10034', '10035', '10036', '10037', '10038', '10039', '10040', '63628'],
    basePriceMultiplier: 1.3
  }
];

// Define pricing for different item categories and their sub-items
export interface SubItem {
  id: string;
  name: string;
  basePrice: number;
  description: string;
}

export interface ItemCategory {
  id: string;
  name: string;
  description: string;
  subItems: SubItem[];
}

export const itemCategories: ItemCategory[] = [
  {
    id: 'furniture',
    name: 'Furniture',
    description: 'Furniture items for removal',
    subItems: [
      {
        id: 'sofa',
        name: 'Sofa',
        basePrice: 75,
        description: 'Standard 3-seat sofa'
      },
      {
        id: 'chair',
        name: 'Chair',
        basePrice: 35,
        description: 'Armchair or dining chair'
      },
      {
        id: 'table',
        name: 'Table',
        basePrice: 50,
        description: 'Dining or coffee table'
      },
      {
        id: 'mattress',
        name: 'Mattress',
        basePrice: 65,
        description: 'Any size mattress'
      },
      {
        id: 'bed-frame',
        name: 'Bed Frame',
        basePrice: 60,
        description: 'Any size bed frame'
      },
      {
        id: 'dresser',
        name: 'Dresser',
        basePrice: 55,
        description: 'Chest of drawers or dresser'
      }
    ]
  },
  {
    id: 'appliances',
    name: 'Appliances',
    description: 'Household appliances for removal',
    subItems: [
      {
        id: 'refrigerator',
        name: 'Refrigerator',
        basePrice: 100,
        description: 'Standard or side-by-side refrigerator'
      },
      {
        id: 'washer',
        name: 'Washing Machine',
        basePrice: 85,
        description: 'Clothes washer'
      },
      {
        id: 'dryer',
        name: 'Dryer',
        basePrice: 85,
        description: 'Clothes dryer'
      },
      {
        id: 'dishwasher',
        name: 'Dishwasher',
        basePrice: 75,
        description: 'Built-in or portable dishwasher'
      },
      {
        id: 'oven',
        name: 'Oven/Stove',
        basePrice: 90,
        description: 'Electric or gas range'
      },
      {
        id: 'microwave',
        name: 'Microwave',
        basePrice: 40,
        description: 'Countertop or built-in microwave'
      }
    ]
  },
  {
    id: 'electronics',
    name: 'Electronics',
    description: 'Electronic devices for removal',
    subItems: [
      {
        id: 'tv',
        name: 'Television',
        basePrice: 60,
        description: 'Flat screen or CRT television'
      },
      {
        id: 'computer',
        name: 'Computer',
        basePrice: 45,
        description: 'Desktop or laptop computer'
      },
      {
        id: 'printer',
        name: 'Printer',
        basePrice: 35,
        description: 'Inkjet or laser printer'
      },
      {
        id: 'stereo',
        name: 'Stereo System',
        basePrice: 50,
        description: 'Home audio system'
      },
      {
        id: 'monitor',
        name: 'Monitor',
        basePrice: 40,
        description: 'Computer monitor'
      }
    ]
  },
  {
    id: 'yard-waste',
    name: 'Yard Waste',
    description: 'Outdoor and garden waste',
    subItems: [
      {
        id: 'branches',
        name: 'Tree Branches',
        basePrice: 50,
        description: 'Bundle of tree branches'
      },
      {
        id: 'leaves',
        name: 'Leaves',
        basePrice: 40,
        description: 'Bags of leaves'
      },
      {
        id: 'grass',
        name: 'Grass Clippings',
        basePrice: 35,
        description: 'Bags of grass clippings'
      },
      {
        id: 'shrubs',
        name: 'Shrubs',
        basePrice: 45,
        description: 'Removed shrubs or bushes'
      }
    ]
  },
  {
    id: 'construction',
    name: 'Construction Debris',
    description: 'Building and renovation waste',
    subItems: [
      {
        id: 'drywall',
        name: 'Drywall',
        basePrice: 120,
        description: 'Sheets or pieces of drywall'
      },
      {
        id: 'lumber',
        name: 'Lumber',
        basePrice: 110,
        description: 'Wood boards and beams'
      },
      {
        id: 'tiles',
        name: 'Tiles',
        basePrice: 100,
        description: 'Floor or ceiling tiles'
      },
      {
        id: 'concrete',
        name: 'Concrete',
        basePrice: 130,
        description: 'Concrete blocks or debris'
      },
      {
        id: 'cabinets',
        name: 'Cabinets',
        basePrice: 115,
        description: 'Kitchen or bathroom cabinets'
      }
    ]
  },
  {
    id: 'household',
    name: 'Household Items',
    description: 'General household junk',
    subItems: [
      {
        id: 'boxes',
        name: 'Boxes of Items',
        basePrice: 65,
        description: 'Boxed household items'
      },
      {
        id: 'toys',
        name: 'Toys',
        basePrice: 50,
        description: 'Children\'s toys and play equipment'
      },
      {
        id: 'books',
        name: 'Books',
        basePrice: 55,
        description: 'Boxes of books'
      },
      {
        id: 'clothing',
        name: 'Clothing',
        basePrice: 45,
        description: 'Bags of clothing'
      },
      {
        id: 'decor',
        name: 'Home Decor',
        basePrice: 60,
        description: 'Decorative items and artwork'
      }
    ]
  }
];

// Keep the original itemTypes for backward compatibility
export interface ItemType {
  id: string;
  name: string;
  basePrice: number;
  description: string;
  volumeMultiplier?: number;
  weightMultiplier?: number;
}

export const itemTypes: ItemType[] = [
  {
    id: 'furniture',
    name: 'Furniture',
    basePrice: 75,
    description: 'Sofas, chairs, tables, beds, etc.',
    volumeMultiplier: 1.0
  },
  {
    id: 'appliances',
    name: 'Appliances',
    basePrice: 100,
    description: 'Refrigerators, washers, dryers, etc.',
    weightMultiplier: 1.2
  },
  {
    id: 'electronics',
    name: 'Electronics',
    basePrice: 60,
    description: 'TVs, computers, printers, etc.',
    weightMultiplier: 0.8
  },
  {
    id: 'yard-waste',
    name: 'Yard Waste',
    basePrice: 50,
    description: 'Branches, leaves, grass clippings, etc.',
    volumeMultiplier: 0.9
  },
  {
    id: 'construction',
    name: 'Construction Debris',
    basePrice: 120,
    description: 'Drywall, lumber, tiles, etc.',
    weightMultiplier: 1.5
  },
  {
    id: 'household',
    name: 'Household Items',
    basePrice: 65,
    description: 'General household junk and clutter',
    volumeMultiplier: 0.9
  },
  {
    id: 'office',
    name: 'Office Equipment',
    basePrice: 85,
    description: 'Desks, chairs, filing cabinets, etc.',
    volumeMultiplier: 1.1
  },
  {
    id: 'other',
    name: 'Other Items',
    basePrice: 90,
    description: 'Miscellaneous items not in other categories',
    volumeMultiplier: 1.0
  }
];

// Calculate price based on zip code and item type
export const calculatePrice = (zipCode: string, itemTypeId: string): number => {
  // Find the region for the zip code
  const region = zipCodeRegions.find(region => region.zipCodes.includes(zipCode));
  const regionMultiplier = region ? region.basePriceMultiplier : 1.0;
  
  // Find the item type
  const item = itemTypes.find(item => item.id === itemTypeId);
  
  if (!item) {
    return 0;
  }
  
  // Calculate the base price with region multiplier
  return Math.round(item.basePrice * regionMultiplier);
};

// Calculate price based on zip code and specific sub-item
export const calculateSubItemPrice = (zipCode: string, categoryId: string, subItemId: string): number => {
  // Find the region for the zip code
  const region = zipCodeRegions.find(region => region.zipCodes.includes(zipCode));
  const regionMultiplier = region ? region.basePriceMultiplier : 1.0;
  
  // Find the category and sub-item
  const category = itemCategories.find(cat => cat.id === categoryId);
  if (!category) return 0;
  
  const subItem = category.subItems.find(item => item.id === subItemId);
  if (!subItem) return 0;
  
  // Calculate the base price with region multiplier
  return Math.round(subItem.basePrice * regionMultiplier);
};

// Validate a zip code
export const isValidZipCode = (zipCode: string): boolean => {
  // Simple validation for 5-digit US zip codes
  return /^\d{5}$/.test(zipCode);
};

// Check if a zip code is in our service area
export const isInServiceArea = (zipCode: string): boolean => {
  return zipCodeRegions.some(region => region.zipCodes.includes(zipCode));
};
